'use client'
import Link from 'next/link'
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { UserText } from '@/components/ui/user-text'
import { Trophy, ChevronRight, Crown } from 'lucide-react'
import { AchievementBadge } from './medallions/achievement-badge'
import { StarIcon } from './medallions/star-icon'
import { useTranslation } from '@/lib/i18n'
import type { Translate } from './catalogue-text'

export interface LeaderboardRow {
  rank: number | null
  userId?: string
  name: string
  avatar: string
  currentStars: number
  isMe?: boolean
  delta?: string
  department?: string | null
}

export interface LeaderboardCardProps {
  /** Current user's first name, used in the All Star banner. */
  firstName: string
  /** Map of rows per scope. Only scopes WITH data get a tab; a single populated
   *  scope renders no tabs at all. */
  scopes: { team?: LeaderboardRow[]; dept?: LeaderboardRow[]; company?: LeaderboardRow[] }
  /** Override a scope's tab label — e.g. the real department name, or
   *  "No department" for the unassigned bucket. */
  scopeLabels?: Partial<Record<'team' | 'dept' | 'company', string>>
  seeAllHref?: string
}

export function LeaderboardCard({ firstName, scopes, scopeLabels, seeAllHref = '/play/leaderboard' }: LeaderboardCardProps) {
  const { t } = useTranslation()
  const SCOPE_LABELS: Record<'team' | 'dept' | 'company', string> = {
    team: scopeLabels?.team ?? t('learner.cards.leaderboard.scopes.team'),
    dept: scopeLabels?.dept ?? t('learner.cards.leaderboard.scopes.dept'),
    company: scopeLabels?.company ?? t('learner.cards.leaderboard.scopes.company'),
  }
  // Only render tabs for scopes that ACTUALLY have data. Duplicating the company
  // board under empty Team/Department tabs presented company ranks as team/dept
  // ranks — a lie. With one populated scope we drop the tabs entirely.
  const available = (['team', 'dept', 'company'] as const).filter((s) => (scopes[s]?.length ?? 0) > 0)
  const [scope, setScope] = useState<'team' | 'dept' | 'company'>('company')
  const activeScope = available.includes(scope) ? scope : available[0] ?? 'company'
  const showTabs = available.length > 1
  const rows = scopes[activeScope] ?? []
  const me = rows.find((r) => r.isMe)
  const meInTop = !!me && rows.slice(0, 5).some((r) => r.isMe)

  return (
    <Card className="relative overflow-hidden h-full">
      <div className="absolute -top-16 -end-16 w-48 h-48 rounded-full opacity-15 blur-3xl bg-primary" />
      <CardHeader className="relative flex flex-row items-center justify-between space-y-0 gap-2 pb-3">
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-500" /> {t('learner.cards.leaderboard.title')}
        </CardTitle>
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
          <Link href={seeAllHref}>{t('learner.cards.common.seeAll')} <ChevronRight className="h-4 w-4 rtl-flip" /></Link>
        </Button>
      </CardHeader>
      <CardContent className="relative">
        <Tabs value={activeScope} onValueChange={(v) => setScope(v as typeof scope)}>
          {showTabs && (
            <TabsList className={`grid w-full ${available.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {available.map((s) => (
                <TabsTrigger key={s} value={s}>{SCOPE_LABELS[s]}</TabsTrigger>
              ))}
            </TabsList>
          )}

          <TabsContent value={activeScope} className={showTabs ? 'mt-4 space-y-3' : 'space-y-3'}>
            {me && me.rank === 1 && (
              <div className="relative overflow-hidden rounded-xl border border-amber-400/40 bg-gradient-to-br from-amber-500/20 via-yellow-500/10 to-orange-500/15 p-3">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <AchievementBadge color="#f59e0b" icon={<Crown className="h-6 w-6" />} size={56} />
                    <div className="absolute -inset-3 -z-10 rounded-full blur-2xl bg-amber-400/40" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">{t('learner.cards.leaderboard.allStar')}</div>
                    <div className="text-sm font-semibold text-foreground">
                      {t('learner.cards.leaderboard.numberOne', { name: firstName })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeScope === 'dept' ? (
              // Department scope: grouped BY department name (a section per
              // department, "No department" last), members ranked within.
              <div className="space-y-3">
                {rows.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4">{t('learner.cards.leaderboard.emptyDept')}</div>
                )}
                {groupByDept(rows).map((g) => (
                  <div key={g.name ?? NO_DEPARTMENT_KEY} className="space-y-1.5">
                    <div className="flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {g.name ?? t('learner.cards.leaderboard.noDepartment')}
                      <span className="text-muted-foreground/60">· {g.members.length}</span>
                    </div>
                    {g.members.map((row, i) => <Row key={row.userId ?? i} row={row} t={t} />)}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  {rows.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-4">{t('learner.cards.leaderboard.emptyScope')}</div>
                  )}
                  {rows.slice(0, 5).map((row, i) => <Row key={row.userId ?? row.rank ?? i} row={row} t={t} />)}
                </div>

                {me && !meInTop && (
                  <div className="pt-2 border-t border-dashed border-border mt-2">
                    <Row row={me} t={t} />
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}

/** The map key of the unassigned bucket; its label is translated at render. */
const NO_DEPARTMENT_KEY = '\u0000no-department'

/** Group rows by department (named depts A→Z, then the unassigned bucket, whose
 *  `name` is null), re-ranked 1..N within each group and sorted by stars. A
 *  real department that happens to be called "No department" stays a
 *  department of its own. */
function groupByDept(rows: LeaderboardRow[]): { name: string | null; members: LeaderboardRow[] }[] {
  const map = new Map<string, LeaderboardRow[]>()
  for (const r of rows) {
    const key = (r.department ?? '').trim() || NO_DEPARTMENT_KEY
    const arr = map.get(key) ?? []
    arr.push(r)
    map.set(key, arr)
  }
  return [...map.entries()]
    .map(([key, members]) => ({
      name: key === NO_DEPARTMENT_KEY ? null : key,
      members: [...members]
        .sort((a, b) => (b.currentStars ?? 0) - (a.currentStars ?? 0))
        .map((m, i) => ({ ...m, rank: i + 1 })),
    }))
    .sort((a, b) => (a.name === null ? 1 : b.name === null ? -1 : a.name.localeCompare(b.name)))
}

function Row({ row, t }: { row: LeaderboardRow; t: Translate }) {
  const rankColor =
    row.rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-600 text-white' :
    row.rank === 2 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-white' :
    row.rank === 3 ? 'bg-gradient-to-br from-amber-700 to-orange-800 text-white' :
    'bg-muted text-muted-foreground'
  const delta = row.delta ?? '0'
  const deltaColor = delta.startsWith('+') ? 'text-emerald-500 dark:text-emerald-400'
    : delta.startsWith('-') ? 'text-red-500 dark:text-red-400'
    : 'text-muted-foreground'

  return (
    <div className={
      'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ' +
      (row.isMe ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-muted/60')
    }>
      <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${rankColor}`}>{row.rank ?? '—'}</div>
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">{row.avatar}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate text-foreground">
          {/* A person's name keeps its own direction: a Latin name on an
              Arabic page, or an Arabic one on an English page, beside "(you)". */}
          <UserText>{row.name}</UserText> {row.isMe && <span className="text-xs text-muted-foreground">{t('learner.cards.leaderboard.you')}</span>}
        </div>
      </div>
      <div className={`text-[10px] font-semibold tabular-nums ${deltaColor}`}>{delta !== '0' ? delta : '—'}</div>
      <div className="flex items-center gap-1 font-semibold text-foreground min-w-[60px] justify-end">
        <StarIcon size={13} />
        <span className="tabular-nums">{row.currentStars}</span>
      </div>
    </div>
  )
}
